import React, { useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  GridSize,
  GridSpacing,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import { OrganizationFlagEnum } from '../../../lib/features/FeatureFlag';
import Layout from '../../Layout/Layout';
import LayoutContent from '../../Layout/LayoutContent';
import useFeatureFlag from '../../../lib/hook/useFeatureFlag';

const useStyles = makeStyles((theme: Theme) => ({
  content: {
    marginTop: theme.spacing(3),
  },
  footerDivider: {
    background: theme.palette.divider,
    height: '1px',
    left: 0,
    position: 'absolute',
    width: '100%',
  },
  pageLayoutContained: {
    height: `calc(100% - ${theme.spacing(11)})`,
    maxHeight: '100%',
  },
  roomContainer: {
    // 1140px
    maxWidth: theme.spacing(142.5),
    [theme.breakpoints.up('lg')]: {
      padding: 0,
    },
  },
  stickyContainer: {
    position: 'sticky',
    top: theme.spacing(3),
  },
}));

type ClassKey = 'appBar' | 'container' | 'contentContainer';

export interface RoomContainerProps {
  appBar: React.ReactNode;
  banner?: React.ReactNode;
  center: React.ReactNode;
  classes?: Partial<Record<ClassKey, string>>;
  contained?: boolean;
  disableContainerScroll?: boolean;
  disableContentOverflow?: boolean;
  disableFooter?: boolean;
  disableFooterDivider?: boolean;
  disableStickiness?: boolean;
  fullWidthCenter?: boolean;
  isTiles?: boolean;
  left?: React.ReactNode;
  right?: React.ReactNode;
  spacing?: GridSpacing;
}

const RoomContainer: React.FC<RoomContainerProps> = (
  props: RoomContainerProps
): JSX.Element => {
  const classes = useStyles();

  const hidePoweredBy = useFeatureFlag(OrganizationFlagEnum.HIDE_POWERED_BY);

  const {
    appBar,
    banner,
    center,
    classes: classesProp,
    contained = false,
    disableContentOverflow = false,
    disableFooter = false,
    disableFooterDivider = false,
    disableContainerScroll = false,
    disableStickiness = false,
    fullWidthCenter = false,
    isTiles = false,
    left,
    right,
    spacing = 0,
  } = props;

  const stickyClassname = useMemo(
    () => (disableStickiness ? undefined : classes.stickyContainer),
    [classes, disableStickiness]
  );

  const hasNoSlots = !(left || right || isTiles);

  const renderAppBar = (): React.ReactNode =>
    !contained ? (
      appBar
    ) : (
      <Container
        className={classNames(classes.roomContainer, classesProp?.container)}
        maxWidth={false}
      >
        <>{appBar}</>
      </Container>
    );

  const renderLeftSlot = (): JSX.Element | undefined => {
    if (!left) {
      return undefined;
    }

    return (
      <Grid className={classes.stickyContainer} item xs={12} md={right ? 3 : 4}>
        {React.cloneElement(left as any, {
          className: classes.stickyContainer,
        })}
      </Grid>
    );
  };

  const renderRightSlot = (): JSX.Element | undefined => {
    if (!right) {
      return undefined;
    }

    return (
      <Grid className={stickyClassname} item xs={12} md={left ? 3 : 4}>
        {React.cloneElement(right as any, {
          className: stickyClassname,
        })}
      </Grid>
    );
  };

  const getCenterSlotMediumSize = () => {
    let md: GridSize = 12;

    if (fullWidthCenter) {
      return md;
    }

    const hasBothSlots = left && right;
    const hasOneSlot = left || right;

    if (hasBothSlots) {
      md = 6;
    } else if (hasNoSlots || hasOneSlot) {
      md = 8;
    }

    return md;
  };

  const renderCenterSlot = (): JSX.Element => (
    <Grid item xs={12} md={getCenterSlotMediumSize()}>
      {center}
    </Grid>
  );

  const renderSlots = (): JSX.Element => (
    <Grid
      className={classNames(classes.content, classesProp?.contentContainer)}
      container
      justifyContent={hasNoSlots ? 'center' : undefined}
      spacing={spacing}
    >
      {renderLeftSlot()}
      {renderCenterSlot()}
      {renderRightSlot()}
    </Grid>
  );

  const renderFooter = (footer: React.ReactNode): JSX.Element => {
    if (disableFooter) {
      return <></>;
    }

    if (disableFooterDivider) {
      return <>{footer}</>;
    }

    return (
      <>
        <Box mt={2} />
        <Box className={classes.footerDivider} />
        {footer}
      </>
    );
  };

  return (
    <Layout
      appBar={renderAppBar()}
      disableAppBarRegistration={contained}
      LayoutAppBarProps={{ classes: { root: classesProp?.appBar } }}
      main={!contained}
      overflow={contained}
      showPoweredBy={!hidePoweredBy}
      showPrivacyPolicy
    >
      {(footer) => (
        <LayoutContent
          background="white"
          className={classNames({ [classes.pageLayoutContained]: contained })}
          disableOverflow={disableContentOverflow}
          disablePadding
          disableScroll={disableContainerScroll}
          sx={{ pb: 3 }}
        >
          <Container
            className={classNames(
              classes.roomContainer,
              classesProp?.container
            )}
            maxWidth={false}
          >
            {banner && <Box mt={5}>{banner}</Box>}
            {renderSlots()}
            {renderFooter(footer)}
          </Container>
        </LayoutContent>
      )}
    </Layout>
  );
};

export default RoomContainer;
